// src/screens/TaxScreen.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Brief 34 Section 7 — Your Tax
// Tax breakdown, threshold visualisation, OMB vs employee comparison, SA bills.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useMemo } from 'react';
import { ClientData } from '../types/ClientData';
import { getTaxYearForDate, TAX_YEARS, TaxYear } from '../taxConfig';
import { formatCurrency } from '../utils/format';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

const fmt = formatCurrency;

// ── Full calcTax (same as IncomeScreen — shared logic) ──────────────────────
function calcTax(data: ClientData) {
  const taxYear = data.taxYearOverride ?? getTaxYearForDate(data.sessionDate || new Date().toISOString());
  const r = TAX_YEARS[taxYear];

  const salary     = data.salary || 0;
  const payeIncome = data.payeIncome || 0;
  const rentalNet  = data.rentalIncomeNet || 0;
  const savingsInt = data.savingsInterest || 0;
  const companyDiv = data.dividends || 0;
  const otherDiv   = data.otherDividends || 0;
  const expenses   = (data.expensesReimbursement || 0) * 12;

  const nonSavingsIncome = salary + payeIncome + rentalNet;
  const savingsIncome    = savingsInt;
  const totalDividends   = companyDiv + otherDiv;
  const totalTaxableIncome = nonSavingsIncome + savingsIncome + totalDividends;

  let effectivePA: number = r.personalAllowance;
  if (totalTaxableIncome > 100000) {
    const taper = Math.floor((totalTaxableIncome - 100000) / 2);
    effectivePA = Math.max(0, r.personalAllowance - taper);
  }

  const bandPA = effectivePA;
  const bandBasicTop = r.basicRateLimit;
  const bandHigherTop = r.higherRateLimit;

  function fillBands(amount: number, cursor: number) {
    let remaining = amount;
    let inPA = 0, inBasic = 0, inHigher = 0, inAdditional = 0;
    if (cursor < bandPA && remaining > 0) { const fill = Math.min(remaining, bandPA - cursor); inPA = fill; cursor += fill; remaining -= fill; }
    if (cursor < bandBasicTop && remaining > 0) { const fill = Math.min(remaining, bandBasicTop - cursor); inBasic = fill; cursor += fill; remaining -= fill; }
    if (cursor < bandHigherTop && remaining > 0) { const fill = Math.min(remaining, bandHigherTop - cursor); inHigher = fill; cursor += fill; remaining -= fill; }
    if (remaining > 0) { inAdditional = remaining; cursor += remaining; }
    return { inPA, inBasic, inHigher, inAdditional, newCursor: cursor };
  }

  const ns = fillBands(nonSavingsIncome, 0);
  const sv = fillBands(savingsIncome, ns.newCursor);
  const dv = fillBands(totalDividends, sv.newCursor);

  const itNonSavings = ns.inBasic * r.incomeTaxBasic + ns.inHigher * r.incomeTaxHigher + ns.inAdditional * r.incomeTaxAdditional;

  const highestNonSavingsBand = ns.inAdditional > 0 ? 'additional' : ns.inHigher > 0 ? 'higher' : 'basic';
  const psa = highestNonSavingsBand === 'additional' ? 0 : highestNonSavingsBand === 'higher' ? 500 : 1000;
  const taxableSavings = Math.max(0, savingsIncome - psa);
  const svTaxableBasic = Math.max(0, Math.min(sv.inBasic, taxableSavings));
  const svTaxableHigher = Math.max(0, Math.min(sv.inHigher, taxableSavings - svTaxableBasic));
  const svTaxableAdditional = Math.max(0, taxableSavings - svTaxableBasic - svTaxableHigher);
  const itSavings = svTaxableBasic * r.incomeTaxBasic + svTaxableHigher * r.incomeTaxHigher + svTaxableAdditional * r.incomeTaxAdditional;

  const taxableDivs = Math.max(0, totalDividends - r.dividendAllowance);
  const dvTaxableBasic = Math.min(dv.inBasic, taxableDivs);
  const dvTaxableHigher = Math.min(dv.inHigher, Math.max(0, taxableDivs - dvTaxableBasic));
  const dvTaxableAdditional = Math.max(0, taxableDivs - dvTaxableBasic - dvTaxableHigher);
  const dividendTax = dvTaxableBasic * r.dividendBasicRate + dvTaxableHigher * r.dividendHigherRate + dvTaxableAdditional * r.dividendAdditionalRate;

  const niableSalary = Math.max(0, salary - r.niPrimaryThreshold);
  const niBasicAmount = Math.min(niableSalary, Math.max(0, r.niUpperEarningsLimit - r.niPrimaryThreshold));
  const niUpperAmount = Math.max(0, niableSalary - niBasicAmount);
  const niEmployee = niBasicAmount * r.niEmployeeRate + niUpperAmount * r.niEmployeeUpperRate;

  let slUndergradAnnual = 0;
  const slPlan = data.studentLoanUndergrad || 'none';
  if (slPlan !== 'none') {
    const threshold = slPlan === 'plan1' ? r.slPlan1Threshold : slPlan === 'plan2' ? r.slPlan2Threshold : r.slPlan5Threshold;
    slUndergradAnnual = Math.max(0, totalTaxableIncome - threshold) * r.slUndergradRate;
  }
  const slPostgradAnnual = data.studentLoanPostgrad ? Math.max(0, totalTaxableIncome - r.slPostgradThreshold) * r.slPostgradRate : 0;
  const slTotalAnnual = slUndergradAnnual + slPostgradAnnual;

  const totalAnnualTax = itNonSavings + itSavings + dividendTax + niEmployee + slTotalAnnual;
  const totalMonthlyTax = totalAnnualTax / 12;
  const grossMonthly = (totalTaxableIncome / 12) + (expenses / 12);
  const netMonthly = grossMonthly - totalMonthlyTax;
  const effectivePersonalRate = totalTaxableIncome > 0 ? totalAnnualTax / totalTaxableIncome : 0;

  // Equivalent employee salary (binary search)
  const netAnnualExExpenses = (netMonthly * 12) - expenses;
  function calcNetFromGross(gross: number): number {
    const pa = gross > 100000 ? Math.max(0, r.personalAllowance - Math.floor((gross - 100000) / 2)) : r.personalAllowance;
    const taxable = Math.max(0, gross - pa);
    const basicBand = Math.max(0, r.basicRateLimit - pa);
    const inBasicE = Math.min(taxable, basicBand);
    const inHigherE = Math.min(Math.max(0, taxable - basicBand), r.higherRateLimit - r.basicRateLimit);
    const inAdditionalE = Math.max(0, taxable - basicBand - (r.higherRateLimit - r.basicRateLimit));
    const incomeTax = inBasicE * r.incomeTaxBasic + inHigherE * r.incomeTaxHigher + inAdditionalE * r.incomeTaxAdditional;
    const niable = Math.max(0, gross - r.niPrimaryThreshold);
    const niBasic = Math.min(niable, Math.max(0, r.niUpperEarningsLimit - r.niPrimaryThreshold));
    const niUpper = Math.max(0, niable - niBasic);
    const ni = niBasic * r.niEmployeeRate + niUpper * r.niEmployeeUpperRate;
    return gross - incomeTax - ni;
  }
  let eqLow = 0, eqHigh = netAnnualExExpenses * 3;
  for (let i = 0; i < 50; i++) {
    const mid = (eqLow + eqHigh) / 2;
    if (calcNetFromGross(mid) < netAnnualExExpenses) eqLow = mid; else eqHigh = mid;
  }
  const equivalentGrossSalary = Math.round((eqLow + eqHigh) / 2);

  // Employee tax on equivalent gross
  const employeeTaxOnEquiv = equivalentGrossSalary - calcNetFromGross(equivalentGrossSalary);

  // ── Per-band tax amounts (annual) for component breakdown ────────────────
  const itBasicAnnual = ns.inBasic * r.incomeTaxBasic;
  const itHigherAnnual = ns.inHigher * r.incomeTaxHigher;
  const itAdditionalAnnual = ns.inAdditional * r.incomeTaxAdditional;
  const dvTaxBasicAnnual = dvTaxableBasic * r.dividendBasicRate;
  const dvTaxHigherAnnual = dvTaxableHigher * r.dividendHigherRate;
  const dvTaxAdditionalAnnual = dvTaxableAdditional * r.dividendAdditionalRate;

  return {
    taxYear,
    rates: r,
    totalAnnualTax,
    totalMonthlyTax,
    grossMonthly,
    netMonthly,
    effectivePersonalRate,
    itNonSavings,
    itSavings,
    dividendTax,
    niEmployee,
    slTotalAnnual,
    totalTaxableIncome,
    totalDividends,
    nonSavingsIncome,
    expenses,
    equivalentGrossSalary,
    employeeTaxOnEquiv,
    effectivePA,
    bandPA,
    bandBasicTop,
    bandHigherTop,
    // Per-band components
    itBasicAnnual,
    itHigherAnnual,
    itAdditionalAnnual,
    dvTaxBasicAnnual,
    dvTaxHigherAnnual,
    dvTaxAdditionalAnnual,
  };
}

export default function TaxScreen({ data }: Props) {
  const t = useMemo(() => calcTax(data), [data]);

  // SA payment estimates
  const showSA = data.taxPaymentMethod === 'selfAssessment';
  const saJanPayment = Math.round(t.totalAnnualTax / 2);
  const saJulyPOA = Math.round(t.totalAnnualTax / 2);
  const saSinkingFund = Math.round(t.totalMonthlyTax);

  // Tax saving
  const ombTaxAnnual = t.totalAnnualTax;
  const employeeTaxAnnual = t.employeeTaxOnEquiv;
  const annualSaving = Math.round(employeeTaxAnnual - ombTaxAnnual);
  const monthlySaving = Math.round(annualSaving / 12);

  // Threshold bar: scale to max of higherRateLimit or income, whichever is larger
  const barMax = Math.max(t.rates.higherRateLimit, t.totalTaxableIncome) * 1.05;
  const paPct = (t.rates.personalAllowance / barMax) * 100;
  const basicPct = ((t.rates.basicRateLimit - t.rates.personalAllowance) / barMax) * 100;
  const higherPct = ((t.rates.higherRateLimit - t.rates.basicRateLimit) / barMax) * 100;
  const additionalPct = Math.max(0, 100 - paPct - basicPct - higherPct);
  const incomePct = Math.min(100, (t.totalTaxableIncome / barMax) * 100);

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-6 max-w-5xl mx-auto w-full">
        {/* Header */}
        <div className="text-center mb-6 relative">
          <h2 className="text-2xl font-bold text-ma-navy">Your Tax</h2>
          <p className="text-gray-500 mt-1 text-sm">Where your money goes before you touch it.</p>
          {/* Tax year badge */}
          <div className="absolute top-0 right-0 bg-gray-100 text-gray-500 text-xs rounded-full px-3 py-1">
            {t.taxYear}
          </div>
        </div>

        {/* Gross → Tax → Net waterfall bar */}
        {t.grossMonthly > 0 && (() => {
          const taxPct = Math.max(0, Math.min(100, (t.totalMonthlyTax / t.grossMonthly) * 100));
          const netPct = Math.max(0, 100 - taxPct);
          return (
            <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
              <div className="flex justify-between items-baseline mb-3">
                <h3 className="text-sm font-bold text-ma-navy uppercase tracking-wide">Where Your Gross Goes</h3>
                <span className="text-xs text-gray-400">monthly</span>
              </div>
              <div className="flex h-10 rounded-full overflow-hidden">
                <div
                  className="flex items-center justify-center text-xs font-semibold text-white"
                  style={{ width: `${taxPct}%`, background: '#EF4444' }}
                  title={`Tax: ${fmt(Math.round(t.totalMonthlyTax))}/mo`}
                >
                  {taxPct >= 10 ? `Tax ${taxPct.toFixed(0)}%` : ''}
                </div>
                <div
                  className="flex items-center justify-center text-xs font-semibold text-white"
                  style={{ width: `${netPct}%`, background: '#10B981' }}
                  title={`Net: ${fmt(Math.round(t.netMonthly))}/mo`}
                >
                  {netPct >= 10 ? `Net ${netPct.toFixed(0)}%` : ''}
                </div>
              </div>
              <div className="flex justify-between text-xs mt-3">
                <span className="text-gray-500">Gross <span className="font-semibold text-ma-navy">{fmt(Math.round(t.grossMonthly))}</span>/mo</span>
                <span className="text-red-600">− Tax <span className="font-semibold">{fmt(Math.round(t.totalMonthlyTax))}</span>/mo</span>
                <span className="text-green-700">= Net <span className="font-semibold">{fmt(Math.round(t.netMonthly))}</span>/mo</span>
              </div>
            </div>
          );
        })()}

        {/* Tax Breakdown Card — two-column */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <h3 className="text-sm font-bold text-ma-navy mb-4 uppercase tracking-wide">Tax Breakdown</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 text-xs text-gray-500 font-medium">Tax</th>
                <th className="text-right py-2 text-xs text-gray-500 font-medium">Monthly</th>
                <th className="text-right py-2 text-xs text-gray-500 font-medium">Annual</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-100">
                <td className="py-2 text-gray-700">Income tax on salary & rental</td>
                <td className="py-2 text-right font-medium">{fmt(Math.round(t.itNonSavings / 12))}</td>
                <td className="py-2 text-right font-medium">{fmt(Math.round(t.itNonSavings))}</td>
              </tr>
              {t.dividendTax > 0 && (
                <tr className="border-b border-gray-100">
                  <td className="py-2 text-gray-700">Dividend tax</td>
                  <td className="py-2 text-right font-medium">{fmt(Math.round(t.dividendTax / 12))}</td>
                  <td className="py-2 text-right font-medium">{fmt(Math.round(t.dividendTax))}</td>
                </tr>
              )}
              {t.niEmployee > 0 && (
                <tr className="border-b border-gray-100">
                  <td className="py-2 text-gray-700">National Insurance</td>
                  <td className="py-2 text-right font-medium">{fmt(Math.round(t.niEmployee / 12))}</td>
                  <td className="py-2 text-right font-medium">{fmt(Math.round(t.niEmployee))}</td>
                </tr>
              )}
              {t.itSavings > 0 && (
                <tr className="border-b border-gray-100">
                  <td className="py-2 text-gray-700">Tax on savings interest</td>
                  <td className="py-2 text-right font-medium">{fmt(Math.round(t.itSavings / 12))}</td>
                  <td className="py-2 text-right font-medium">{fmt(Math.round(t.itSavings))}</td>
                </tr>
              )}
              {t.slTotalAnnual > 0 && (
                <tr className="border-b border-gray-100">
                  <td className="py-2 text-gray-700">Student loan repayment</td>
                  <td className="py-2 text-right font-medium">{fmt(Math.round(t.slTotalAnnual / 12))}</td>
                  <td className="py-2 text-right font-medium">{fmt(Math.round(t.slTotalAnnual))}</td>
                </tr>
              )}
              <tr className="border-t-2 border-gray-200">
                <td className="py-2 font-bold text-ma-navy">Total tax</td>
                <td className="py-2 text-right font-bold text-ma-navy">{fmt(Math.round(t.totalMonthlyTax))}</td>
                <td className="py-2 text-right font-bold text-ma-navy">{fmt(Math.round(t.totalAnnualTax))}</td>
              </tr>
            </tbody>
          </table>
          <div className="flex justify-between text-sm mt-2">
            <span className="text-gray-500">Effective rate</span>
            <span className="font-medium text-ma-navy">{(t.effectivePersonalRate * 100).toFixed(1)}%</span>
          </div>
        </div>

        {/* Tax by Component Card — two-column */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <h3 className="text-sm font-bold text-ma-navy mb-4 uppercase tracking-wide">Tax by Component</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 text-xs text-gray-500 font-medium">Component</th>
                <th className="text-right py-2 text-xs text-gray-500 font-medium">Monthly</th>
                <th className="text-right py-2 text-xs text-gray-500 font-medium">Annual</th>
              </tr>
            </thead>
            <tbody>
              {t.itBasicAnnual > 0 && (
                <tr className="border-b border-gray-100">
                  <td className="py-2 text-gray-700">Income tax — salary & rental ({(t.rates.incomeTaxBasic * 100).toFixed(0)}%)</td>
                  <td className="py-2 text-right font-medium">{fmt(Math.round(t.itBasicAnnual / 12))}</td>
                  <td className="py-2 text-right font-medium">{fmt(Math.round(t.itBasicAnnual))}</td>
                </tr>
              )}
              {t.itHigherAnnual > 0 && (
                <tr className="border-b border-gray-100">
                  <td className="py-2 text-gray-700">Income tax — higher rate ({(t.rates.incomeTaxHigher * 100).toFixed(0)}%)</td>
                  <td className="py-2 text-right font-medium">{fmt(Math.round(t.itHigherAnnual / 12))}</td>
                  <td className="py-2 text-right font-medium">{fmt(Math.round(t.itHigherAnnual))}</td>
                </tr>
              )}
              {t.itAdditionalAnnual > 0 && (
                <tr className="border-b border-gray-100">
                  <td className="py-2 text-red-600">Income tax — additional rate ({(t.rates.incomeTaxAdditional * 100).toFixed(0)}%)</td>
                  <td className="py-2 text-right font-medium text-red-600">{fmt(Math.round(t.itAdditionalAnnual / 12))}</td>
                  <td className="py-2 text-right font-medium text-red-600">{fmt(Math.round(t.itAdditionalAnnual))}</td>
                </tr>
              )}
              {t.dvTaxBasicAnnual > 0 && (
                <tr className="border-b border-gray-100">
                  <td className="py-2 text-gray-700">Dividend tax — basic rate ({(t.rates.dividendBasicRate * 100).toFixed(2)}%)</td>
                  <td className="py-2 text-right font-medium">{fmt(Math.round(t.dvTaxBasicAnnual / 12))}</td>
                  <td className="py-2 text-right font-medium">{fmt(Math.round(t.dvTaxBasicAnnual))}</td>
                </tr>
              )}
              {t.dvTaxHigherAnnual > 0 && (
                <tr className="border-b border-gray-100">
                  <td className="py-2 text-amber-600">Dividend tax — higher rate ({(t.rates.dividendHigherRate * 100).toFixed(2)}%)</td>
                  <td className="py-2 text-right font-medium text-amber-600">{fmt(Math.round(t.dvTaxHigherAnnual / 12))}</td>
                  <td className="py-2 text-right font-medium text-amber-600">{fmt(Math.round(t.dvTaxHigherAnnual))}</td>
                </tr>
              )}
              {t.dvTaxAdditionalAnnual > 0 && (
                <tr className="border-b border-gray-100">
                  <td className="py-2 text-red-600">Dividend tax — additional rate ({(t.rates.dividendAdditionalRate * 100).toFixed(2)}%)</td>
                  <td className="py-2 text-right font-medium text-red-600">{fmt(Math.round(t.dvTaxAdditionalAnnual / 12))}</td>
                  <td className="py-2 text-right font-medium text-red-600">{fmt(Math.round(t.dvTaxAdditionalAnnual))}</td>
                </tr>
              )}
              {t.niEmployee > 0 && (
                <tr className="border-b border-gray-100">
                  <td className="py-2 text-gray-700">National Insurance ({(t.rates.niEmployeeRate * 100).toFixed(0)}%)</td>
                  <td className="py-2 text-right font-medium">{fmt(Math.round(t.niEmployee / 12))}</td>
                  <td className="py-2 text-right font-medium">{fmt(Math.round(t.niEmployee))}</td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Higher Rate Total */}
          {(() => {
            const totalHigherRateMonthly = Math.round((t.itHigherAnnual + t.itAdditionalAnnual + t.dvTaxHigherAnnual + t.dvTaxAdditionalAnnual) / 12);
            const totalHigherRateAnnual = totalHigherRateMonthly * 12;
            if (totalHigherRateMonthly <= 0) return null;
            return (
              <>
                <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl px-5 py-3 flex items-center justify-between">
                  <span className="text-sm font-bold text-ma-navy">Higher rate total</span>
                  <div className="text-right">
                    <span className="text-sm font-bold text-ma-navy">{fmt(totalHigherRateMonthly)}/mo</span>
                    <span className="text-xs text-gray-500 ml-2">{fmt(totalHigherRateAnnual)}/yr</span>
                  </div>
                </div>
                <p className="text-sm text-amber-700 mt-3">
                  {fmt(totalHigherRateAnnual)}/year going to HMRC at higher rates. The pension lever directly reduces this.
                </p>
              </>
            );
          })()}
        </div>

        {/* Tax Threshold Visualisation */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <h3 className="text-sm font-bold text-ma-navy mb-4 uppercase tracking-wide">Where You Sit</h3>
          <div className="relative">
            <div className="flex h-8 rounded-full overflow-hidden">
              <div className="bg-gray-300" style={{ width: `${paPct}%` }} title="Personal Allowance" />
              <div className="bg-blue-300" style={{ width: `${basicPct}%` }} title="Basic Rate" />
              <div className="bg-amber-400" style={{ width: `${higherPct}%` }} title="Higher Rate" />
              {additionalPct > 0 && (
                <div className="bg-red-400" style={{ width: `${additionalPct}%` }} title="Additional Rate" />
              )}
            </div>
            {/* Income marker */}
            <div
              className="absolute top-0 h-8 w-0.5 bg-ma-navy"
              style={{ left: `${incomePct}%` }}
            />
            <div
              className="absolute top-9 transform -translate-x-1/2 text-xs font-bold text-ma-navy whitespace-nowrap"
              style={{ left: `${Math.min(90, Math.max(10, incomePct))}%` }}
            >
              You: {fmt(t.totalTaxableIncome)}
            </div>
          </div>
          {/* Labels */}
          <div className="flex justify-between text-[10px] text-gray-400 mt-6">
            <span>{fmt(t.rates.personalAllowance)} PA</span>
            <span>{fmt(t.rates.basicRateLimit)} Basic</span>
            <span>{fmt(t.rates.higherRateLimit)} Higher</span>
          </div>
          <div className="flex gap-3 mt-3 text-[10px]">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-gray-300" />0% (PA)</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-blue-300" />20% (Basic)</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-amber-400" />40% (Higher)</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-red-400" />45% (Additional)</span>
          </div>
        </div>

        {/* OMB vs Employee Comparison */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <h3 className="text-sm font-bold text-ma-navy mb-4 uppercase tracking-wide">OMB vs Employee</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 text-gray-500 font-medium"></th>
                  <th className="text-right py-2 text-ma-navy font-semibold">OMB Director ({data.clientName?.split(' ')[0] || 'You'})</th>
                  <th className="text-right py-2 text-gray-500 font-medium">Employee Equivalent</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-100">
                  <td className="py-2 text-gray-600">Gross income</td>
                  <td className="py-2 text-right font-medium">{fmt(Math.round(t.totalTaxableIncome / 12))}/mo</td>
                  <td className="py-2 text-right font-medium text-gray-500">{fmt(Math.round(t.equivalentGrossSalary / 12))}/mo</td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-2 text-gray-600">Total tax</td>
                  <td className="py-2 text-right font-medium">{fmt(Math.round(t.totalMonthlyTax))}/mo</td>
                  <td className="py-2 text-right font-medium text-gray-500">{fmt(Math.round(t.employeeTaxOnEquiv / 12))}/mo</td>
                </tr>
                <tr>
                  <td className="py-2 font-semibold text-green-700">Tax saving</td>
                  <td className="py-2 text-right font-bold text-green-600">{fmt(monthlySaving)}/mo</td>
                  <td className="py-2 text-right text-gray-400">—</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-sm text-gray-600 mt-4">
            Your structure saves you <span className="font-bold text-green-600">{fmt(annualSaving)}</span> per year compared to an equivalent employee. You also have levers an employee doesn't.
          </p>
        </div>

        {/* OMB Control Note */}
        <div className="border-l-4 border-ma-navy bg-blue-50 rounded-r-2xl p-5 mb-6">
          <p className="text-sm text-gray-700 leading-relaxed">
            Unlike an employee, you can influence your tax rate. Taking less in dividends this year — and redirecting that to a company pension instead — reduces both your personal tax and your company's tax bill. We'll model this today.
          </p>
        </div>

        {/* SA Bills */}
        {showSA && (
          <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
            <h3 className="text-sm font-bold text-ma-navy mb-4 uppercase tracking-wide">Self Assessment</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-700">January payment</span>
                <span className="font-medium">{fmt(saJanPayment)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">July payment on account</span>
                <span className="font-medium">{fmt(saJulyPOA)}</span>
              </div>
              <div className="border-t border-gray-200 pt-2 flex justify-between">
                <span className="text-gray-700 font-medium">Suggested monthly sinking fund</span>
                <span className="font-bold text-ma-navy">{fmt(saSinkingFund)}/month</span>
              </div>
            </div>
          </div>
        )}

        {/* Source */}
        <div className="text-center text-[10px] text-gray-400 mb-6">
          Tax rates from taxConfig ({t.taxYear}). Used for planning context only — not formal tax advice.
        </div>
      </div>
    </div>
  );
}
